import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api'; // baseURL에 /api/ 포함된 기존 axios 인스턴스

/**
 * 경영평가 > 연도별 평가편람 목록 + 업로드
 */
export default function EvaluationManualList() {
  const navigate = useNavigate();
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    year: new Date().getFullYear(),
    target_year: new Date().getFullYear() - 1,
    title: '금천일자리(주) 경영실적평가 편람',
    page_offset: 0,
    note: '',
    pdf_file: null,
  });
  const [saving, setSaving] = useState(false);

  const fetchManuals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('evaluation/manuals/');
      setManuals(res.data.results ?? res.data);
    } catch (e) {
      console.error(e);
      alert('편람 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchManuals(); }, [fetchManuals]);

  const handleSubmit = async () => {
    if (!form.pdf_file) { alert('PDF 파일을 선택해주세요.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== '') fd.append(k, v);
      });
      await api.post('evaluation/manuals/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowForm(false);
      setForm((f) => ({ ...f, pdf_file: null, note: '' }));
      fetchManuals();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.year?.[0] ?? '업로드에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`${m.year}년 편람을 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`evaluation/manuals/${m.id}/`);
      fetchManuals();
    } catch (e) {
      console.error(e);
      alert('삭제 권한이 없거나 삭제에 실패했습니다.');
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>연도별 경영평가 편람</h2>
        <button
          onClick={() => setShowForm((v) => !v)}
          style={{
            display: 'inline-block',
            width: 'auto',
            flexShrink: 0,
            padding: '6px 14px',
            fontSize: 13,
            whiteSpace: 'nowrap',
          }}
        >
          {showForm ? '닫기' : '+ 편람 업로드'}
        </button>
      </div>
      


      {showForm && (
        <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>평가연도
              <input type="number" value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value, target_year: e.target.value - 1 })} />
            </label>
            <label>평가대상연도(실적연도)
              <input type="number" value={form.target_year}
                onChange={(e) => setForm({ ...form, target_year: e.target.value })} />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>제목
              <input type="text" value={form.title} style={{ width: '100%' }}
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </label>
            <label>페이지 보정값
              <input type="number" value={form.page_offset} title="편람 인쇄 페이지와 PDF 실제 페이지의 차이"
                onChange={(e) => setForm({ ...form, page_offset: e.target.value })} />
            </label>
            <label>PDF 파일
              <input type="file" accept="application/pdf"
                onChange={(e) => setForm({ ...form, pdf_file: e.target.files[0] })} />
            </label>
          </div>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button onClick={handleSubmit} disabled={saving}>
              {saving ? '업로드 중…' : '저장'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p>불러오는 중…</p>
      ) : manuals.length === 0 ? (
        <p style={{ color: '#888' }}>등록된 편람이 없습니다. 우측 상단에서 PDF를 업로드해주세요.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #333', textAlign: 'left' }}>
              <th style={{ padding: 8 }}>평가연도</th>
              <th style={{ padding: 8 }}>실적연도</th>
              <th style={{ padding: 8 }}>제목</th>
              <th style={{ padding: 8 }}>등록자</th>
              <th style={{ padding: 8 }}>등록일</th>
              <th style={{ padding: 8 }} />
            </tr>
          </thead>
          <tbody>
            {manuals.map((m) => (
              <tr key={m.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: 8, fontWeight: 600 }}>{m.year}년</td>
                <td style={{ padding: 8 }}>{m.target_year}년</td>
                <td style={{ padding: 8 }}>{m.title}</td>
                <td style={{ padding: 8 }}>{m.uploaded_by_name}</td>
                <td style={{ padding: 8 }}>{m.uploaded_at?.slice(0, 10)}</td>

                <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => navigate(`/evaluation/manuals/${m.id}/viewer`)}
                    style={{ display: 'inline-block', width: 'auto', padding: '4px 10px', fontSize: 12, marginRight: 6 }}
                  >
                    PDF 보기
                  </button>
                  <button
                    onClick={() => navigate(`/evaluation/manuals/${m.id}/indicators`)}
                    style={{ display: 'inline-block', width: 'auto', padding: '4px 10px', fontSize: 12, marginRight: 6 }}
                  >
                    지표 보기
                  </button>
                  <button
                    onClick={() => handleDelete(m)}
                    style={{ display: 'inline-block', width: 'auto', padding: '4px 10px', fontSize: 12, color: '#c00' }}
                  >
                    삭제
                  </button>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}